'use client';

import { getCachedSession } from '@/features/auth/utils/sessionCache';
import { io, Socket } from 'socket.io-client';
import { SocketEvents } from '../types/api';

class ChatSocketService {
  private socket: Socket<SocketEvents> | null = null;
  private connectionPromise: Promise<Socket<SocketEvents>> | null = null;
  private isConnecting = false;

  async connect(): Promise<Socket<SocketEvents>> {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (this.isConnecting) {
      await new Promise((resolve) => {
        const checkConnection = () => {
          if (this.socket?.connected || !this.isConnecting) {
            resolve(void 0);
          } else {
            setTimeout(checkConnection, 2000);
          }
        };
        checkConnection();
      });
      return this.socket!;
    }

    this.connectionPromise = this.createConnection();
    return this.connectionPromise;
  }

  private async createConnection(): Promise<Socket<SocketEvents>> {
    this.isConnecting = true;

    try {
      // Wait for session to be available with retry
      let session = await getCachedSession();
      let retries = 0;
      const maxRetries = 5;

      while (!session?.user?.accessToken && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        session = await getCachedSession();
        retries++;
      }

      if (!session?.user?.accessToken) {
        throw new Error('No access token available after retries');
      }

      const socketUrl = process.env.NEXT_PUBLIC_SOCKET_URL || 'http://localhost:3004';

      this.socket = io(`${socketUrl}/chat`, {
        auth: {
          token: session.user.accessToken,
        },
        transports: ['websocket', 'polling'],
        timeout: 10000,
      });

      return new Promise((resolve, reject) => {
        this.socket!.on('connect', () => {
          this.isConnecting = false;
          this.connectionPromise = null;
          resolve(this.socket!);
        });

        this.socket!.on('connect_error', (error) => {
          this.isConnecting = false;
          this.connectionPromise = null;
          reject(error);
        });

        this.socket!.on('disconnect', () => {
          this.isConnecting = false;
          this.connectionPromise = null;
        });
      });
    } catch (error) {
      this.isConnecting = false;
      this.connectionPromise = null;
      throw error;
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectionPromise = null;
    this.isConnecting = false;
  }

  async getSocket(): Promise<Socket<SocketEvents>> {
    if (!this.socket?.connected) {
      await this.connect();
    }
    return this.socket!;
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // Event Listeners
  async onChatsList(callback: (data: Parameters<SocketEvents['chatsList']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('chatsList', callback);
  }

  async onChatStarted(callback: (data: Parameters<SocketEvents['chatStarted']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('chatStarted', callback);
  }

  async onNewChatCreated(callback: (data: Parameters<SocketEvents['newChatCreated']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('newChatCreated', callback);
  }

  async onJoinedRoom(callback: (data: Parameters<SocketEvents['joinedRoom']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('joinedRoom', callback);
  }

  async onLeftRoom(callback: (data: Parameters<SocketEvents['leftRoom']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('leftRoom', callback);
  }

  async onNewMessage(callback: (data: Parameters<SocketEvents['newMessage']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('newMessage', callback);
  }

  async onChatHistory(callback: (data: Parameters<SocketEvents['chatHistory']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('chatHistory', callback);
  }

  async onUserDisconnected(callback: (data: Parameters<SocketEvents['userDisconnected']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('userDisconnected', callback);
  }

  async onChatListUpdated(callback: (data: Parameters<SocketEvents['chatListUpdated']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('chatListUpdated', callback);
  }

  async onError(callback: (data: Parameters<SocketEvents['error']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('error', callback);
  }

  // Event Emitters
  async startChat(data: Parameters<SocketEvents['startChat']>[0]): Promise<void> {
    const socket = await this.getSocket();
    socket.emit('startChat', data);
  }

  async joinRoom(data: Parameters<SocketEvents['joinRoom']>[0]): Promise<void> {
    const socket = await this.getSocket();
    socket.emit('joinRoom', data);
  }

  async leaveRoom(data: Parameters<SocketEvents['leaveRoom']>[0]): Promise<void> {
    const socket = await this.getSocket();
    socket.emit('leaveRoom', data);
  }

  async sendMessage(data: Parameters<SocketEvents['sendMessage']>[0]): Promise<void> {
    const socket = await this.getSocket();
    socket.emit('sendMessage', data);
  }

  async getMyChats(): Promise<void> {
    const socket = await this.getSocket();
    socket.emit('getMyChats');
  }

  async getChatHistory(data: Parameters<SocketEvents['getChatHistory']>[0]): Promise<void> {
    const socket = await this.getSocket();
    socket.emit('getChatHistory', data);
  }

  async subscribeToMyChats(): Promise<void> {
    const socket = await this.getSocket();
    socket.emit('subscribeToMyChats');
  }

  async markMessagesAsRead(data: Parameters<SocketEvents['markMessagesAsRead']>[0]): Promise<void> {
    const socket = await this.getSocket();
    socket.emit('markMessagesAsRead', data);
  }

  async getUnreadCount(data: Parameters<SocketEvents['getUnreadCount']>[0]): Promise<void> {
    const socket = await this.getSocket();
    socket.emit('getUnreadCount', data);
  }

  async onMessagesMarkedAsRead(callback: (data: Parameters<SocketEvents['messagesMarkedAsRead']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('messagesMarkedAsRead', callback);
  }

  async onUnreadCount(callback: (data: Parameters<SocketEvents['unreadCount']>[0]) => void): Promise<void> {
    const socket = await this.getSocket();
    socket.on('unreadCount', callback);
  }

  // Utility
  async off(event: keyof SocketEvents, callback?: (...args: unknown[]) => void): Promise<void> {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }
}

export const chatSocketService = new ChatSocketService();